import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Layers, Cpu, Database, Sparkles } from "lucide-react";
import PartnerMarquee from "@/components/site/PartnerMarquee";
import RotatingWords from "@/components/site/RotatingWords";
import CountUp from "@/components/site/CountUp";
import LiveTicker from "@/components/site/LiveTicker";
import TextMarquee from "@/components/site/TextMarquee";
import { IMAGES, INDUSTRIES, CAPABILITIES, COMPANY } from "@/constants/siteData";

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative pt-32 md:pt-40 pb-24 md:pb-32 overflow-hidden">
        <div className="container-page relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-end">
            <div className="lg:col-span-8">
              <p className="eyebrow mb-6">
                <span className="inline-block h-2 w-2 mr-1 align-middle bg-gradient-to-br from-[#1E5AF6] to-[#16A374]" />{" "}
                Global · Digital &amp; AI advisory
              </p>
              {/* Credibility badges */}
              <div className="mb-8 flex flex-wrap gap-2">
                {[
                  { label: "Oracle Fusion Cloud Partner", color: "#1E5AF6" },
                  { label: "AI-First Delivery", color: "#16A374" },
                  { label: "Dubai · Riyadh · NYC · Chennai", color: "#0B1220" },
                ].map((b) => (
                  <span
                    key={b.label}
                    data-testid={`home-hero-badge-${b.label.split(" ")[0].toLowerCase()}`}
                    className="inline-flex items-center gap-2 border border-[#e5e7eb] bg-white px-3 py-1.5 font-mono text-[10px] tracking-[0.18em] uppercase text-[#0B1220]"
                  >
                    <span className="inline-block h-1.5 w-1.5 rounded-full" style={{ background: b.color }} />
                    {b.label}
                  </span>
                ))}
              </div>
              <h1
                data-testid="home-hero-title"
                className="font-display font-extrabold text-5xl sm:text-6xl md:text-7xl lg:text-[92px] leading-[0.9] tracking-tighter"
              >
                Digital,
                <br />
                Technology
                <br />
                <span className="grad-text">&amp;</span>{" "}
                <RotatingWords
                  words={["Data.", "AI.", "Agents.", "Cloud."]}
                  className="grad-text"
                  testId="home-hero-rotator"
                />
              </h1>
              <p className="mt-6 font-mono text-xs tracking-[0.24em] uppercase text-[#16A374]">
                AI · woven across every enterprise application
              </p>
              <p className="mt-8 max-w-xl text-lg md:text-xl text-[#4B5563] leading-relaxed">
                Delivering clarity in a cluttered business environment. DigiTrans
                partners with leading organizations to design and deliver
                transformative solutions across digital innovation, applied AI,
                software modernization, agile transformation, and enterprise
                technology.
              </p>
              <div className="mt-12 flex flex-wrap gap-4">
                <a
                  href={`mailto:${COMPANY.email}?subject=Discovery`}
                  data-testid="home-hero-cta-connect"
                  className="group relative shine box-elevated inline-flex items-center gap-2 bg-[#0B1220] text-white px-7 py-4 text-sm font-semibold tracking-wide hover:bg-[#1E5AF6] transition-colors"
                >
                  Start a discovery
                  <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                </a>
                <Link
                  to="/capabilities"
                  data-testid="home-hero-cta-capabilities"
                  className="group inline-flex items-center gap-2 border border-[#0B1220] px-7 py-4 text-sm font-semibold tracking-wide hover:bg-[#0B1220] hover:text-white transition-colors"
                >
                  Explore capabilities
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-4 relative">
              <div className="relative aspect-[3/4] w-full overflow-hidden grad-border box-elevated">
                <img
                  src={IMAGES.hero}
                  alt="Dubai modern architecture"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0B1220]/10 to-[#0B1220]/70" />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#1E5AF6]/20 via-transparent to-[#16A374]/15 mix-blend-screen" />
                <div className="absolute bottom-6 left-6 right-6 text-white">
                  <p className="font-mono text-[10px] tracking-[0.3em] uppercase opacity-80">
                    DXB · 25.2048° N
                  </p>
                  <p className="font-display text-2xl font-bold mt-2 leading-tight">
                    Built where global business meets ambition.
                  </p>
                </div>
                {/* Live ticker overlay */}
                <div className="absolute top-4 left-4 right-4">
                  <LiveTicker
                    testId="home-hero-ticker"
                    items={[
                      { time: "just now", badge: "AI", text: "Fusion AR copilot deployed for a MENA retailer", color: "#23D89A" },
                      { time: "1h ago", badge: "GenAI", text: "OCI Generative AI + RAG assistant live for CFO office", color: "#23D89A" },
                      { time: "3h ago", badge: "Oracle", text: "Fusion Cloud go-live · Financials & SCM · KSA", color: "#1E5AF6" },
                      { time: "yesterday", badge: "Data", text: "23ai Vector Search rolled out on OCI", color: "#1E5AF6" },
                    ]}
                  />
                </div>
              </div>
              <div className="hidden lg:grid grid-cols-3 gap-0 border border-[#e5e7eb] mt-6 bg-white box-elevated">
                {[
                  { k: "16+", v: "Enterprise clients", c: "#1E5AF6" },
                  { k: "08", v: "Capabilities", c: "#16A374" },
                  { k: "24/7", v: "Managed services", c: "#0B1220" },
                ].map((s, i) => (
                  <div
                    key={s.k}
                    className={`p-5 ${i < 2 ? "border-r border-[#e5e7eb]" : ""}`}
                  >
                    <p
                      className="font-display text-3xl font-extrabold tracking-tighter"
                      style={{ color: s.c }}
                    >
                      {s.k}
                    </p>
                    <p className="text-xs text-[#4B5563] mt-1 leading-tight">
                      {s.v}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* soft aurora backdrop — blue + green */}
        <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-24 -left-24 h-[520px] w-[520px] rounded-full bg-[#1E5AF6]/10 blur-[120px] aurora" />
          <div className="absolute top-40 right-0 h-[420px] w-[420px] rounded-full bg-[#16A374]/10 blur-[120px] aurora" />
        </div>
        {/* subtle grid backdrop */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-[0.035]"
          style={{
            backgroundImage:
              "linear-gradient(#0B1220 1px, transparent 1px), linear-gradient(90deg, #0B1220 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
      </section>

      <PartnerMarquee />

      {/* IMPACT NUMBERS */}
      <section
        data-testid="home-impact-numbers"
        className="py-16 md:py-20 border-b border-[#e5e7eb]"
      >
        <div className="container-page grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { k: "16+", v: "Enterprise clients partnered", color: "#1E5AF6" },
            { k: "12", v: "Oracle capability tracks", color: "#16A374" },
            { k: "07", v: "Industries served globally", color: "#0B1220" },
            { k: "24/7", v: "Managed & advisory support", color: "#1E5AF6" },
          ].map((s) => (
            <div key={s.k} className="border-l-2 pl-4 md:pl-6" style={{ borderColor: s.color }}>
              <div style={{ color: s.color }}>
                <CountUp
                  value={s.k}
                  className="font-display text-5xl md:text-6xl font-extrabold tracking-tighter block"
                  testId={`home-impact-${s.k}`}
                />
              </div>
              <p className="text-sm text-[#4B5563] mt-2 leading-snug max-w-[220px]">
                {s.v}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* VALUE PROPOSITION — four coloured pillars */}
      <section className="py-24 md:py-32">
        <div className="container-page">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14 items-end">
            <div className="md:col-span-7">
              <p className="eyebrow mb-6">A · The DigiTrans difference</p>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter leading-[1.05]">
                Four pillars.
                <br />
                <span className="grad-text">One clear operating model.</span>
              </h2>
            </div>
            <div className="md:col-span-5">
              <p className="text-[#4B5563] text-lg leading-relaxed">
                We combine strategy, engineering, data and AI under a single
                collaborative model — designed to move enterprises from ambition
                to measurable outcomes.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              {
                Icon: Layers,
                title: "Digital",
                body: "Design, build & scale digital experiences and modern operating models tied to business outcomes.",
                color: "#1E5AF6",
              },
              {
                Icon: Cpu,
                title: "Technology",
                body: "Program management, implementation, integration & AMS across the enterprise application stack.",
                color: "#16A374",
              },
              {
                Icon: Database,
                title: "Data",
                body: "Modern data platforms, warehousing, ETL, governance and analytics that decision-makers trust.",
                color: "#0B1220",
              },
              {
                Icon: Sparkles,
                title: "AI",
                body: "Copilots, GenAI, agentic apps and MLOps — safely embedded across Oracle Fusion and beyond.",
                color: "#1E5AF6",
                accentTop: true,
              },
            ].map(({ Icon, title, body, color, accentTop }) => (
              <div
                key={title}
                data-testid={`home-pillar-${title.toLowerCase()}`}
                className="group relative shine box-elevated border border-[#e5e7eb] bg-white p-7 min-h-[260px] flex flex-col"
              >
                {accentTop && (
                  <span className="absolute top-0 left-0 h-[3px] w-full bg-gradient-to-r from-[#1E5AF6] via-[#23D89A] to-[#16A374]" />
                )}
                <div
                  className="inline-flex h-11 w-11 items-center justify-center mb-5"
                  style={{ background: `${color}12`, color }}
                >
                  <Icon className="w-5 h-5" strokeWidth={2.25} />
                </div>
                <p
                  className="font-mono text-[10px] tracking-[0.24em] uppercase mb-2"
                  style={{ color }}
                >
                  Pillar
                </p>
                <h3 className="font-display text-2xl font-bold mb-3">{title}</h3>
                <p className="text-sm text-[#4B5563] leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI-FIRST STRIP */}
      <section
        data-testid="home-ai-strip"
        className="relative overflow-hidden bg-ink-deep text-white"
      >
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute -top-24 left-1/4 h-[420px] w-[420px] rounded-full bg-[#1E5AF6]/30 blur-[110px] aurora" />
          <div className="absolute bottom-0 right-0 h-[420px] w-[420px] rounded-full bg-[#16A374]/25 blur-[110px] aurora" />
        </div>
        <div className="container-page relative py-20 md:py-28 grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <p className="eyebrow text-[#23D89A] mb-5">AI · everywhere it earns its keep</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tighter leading-[1.02]">
              Artificial intelligence,
              <br />
              <span className="grad-text">woven through every system.</span>
            </h2>
            <p className="mt-8 max-w-2xl text-white/75 text-lg leading-relaxed">
              We embed AI, ML and Generative AI into ERP, CX, HCM, supply-chain
              and finance systems — Oracle Fusion copilots, RAG-based knowledge
              assistants, predictive analytics, anomaly detection, and MLOps
              foundations that make AI safe, governed and enterprise-ready.
            </p>
          </div>
          <div className="md:col-span-5">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { k: "Copilots", v: "Inside Oracle & custom apps" },
                { k: "GenAI + RAG", v: "Knowledge & policy assistants" },
                { k: "Forecasting", v: "Demand, cash, workforce" },
                { k: "MLOps", v: "Governed, observable, scalable" },
              ].map((p) => (
                <li
                  key={p.k}
                  className="relative shine shine-dark box-elevated-dark border border-white/10 bg-white/[0.03] backdrop-blur-sm p-5"
                >
                  <p className="font-display text-lg font-bold">{p.k}</p>
                  <p className="text-white/60 text-sm mt-1 leading-relaxed">{p.v}</p>
                </li>
              ))}
            </ul>
            <Link
              to="/capabilities"
              data-testid="home-ai-cta"
              className="group relative shine shine-dark box-elevated-dark mt-8 inline-flex items-center gap-2 bg-white text-[#0B1220] px-6 py-3 text-sm font-semibold hover:bg-[#16A374] hover:text-white transition-colors"
            >
              See how AI plugs in
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* CAPABILITIES */}
      <section className="bg-[#f9fafb] py-24 md:py-32 border-y border-[#e5e7eb]">
        <div className="container-page">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-16">
            <div>
              <p className="eyebrow mb-4">C · Capabilities</p>
              <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter">
                Where our advisory
                <br /> creates the most impact.
              </h2>
            </div>
            <Link
              to="/capabilities"
              data-testid="home-capabilities-view-all"
              className="text-sm font-semibold link-underline inline-flex items-center gap-2"
            >
              View all capabilities <ArrowRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 border-t border-l border-[#e5e7eb] box-elevated bg-white">
            {CAPABILITIES.map((cap) => {
              const isAI = cap.key === "ai";
              return (
                <div
                  key={cap.key}
                  data-testid={`home-capability-${cap.key}`}
                  className={`group relative shine border-r border-b border-[#e5e7eb] p-8 bg-white transition-colors duration-300 min-h-[240px] flex flex-col ${
                    isAI
                      ? "hover:bg-gradient-to-br hover:from-[#0B1220] hover:to-[#0E1E5A] hover:text-white"
                      : "hover:bg-[#0B1220] hover:text-white"
                  }`}
                >
                  {isAI && (
                    <span className="absolute top-0 left-0 h-[3px] w-full bg-gradient-to-r from-[#1E5AF6] via-[#23D89A] to-[#16A374]" />
                  )}
                  <p className="font-mono text-xs opacity-60 mb-3">{cap.number}</p>
                  {isAI && (
                    <span
                      data-testid="home-capability-ai-badge"
                      className="inline-flex w-fit items-center gap-1.5 border border-[#16A374]/40 text-[#16A374] group-hover:text-[#23D89A] group-hover:border-[#23D89A]/50 px-2 py-1 font-mono text-[10px] tracking-[0.2em] uppercase mb-4"
                    >
                      <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#16A374] group-hover:bg-[#23D89A]" />
                      Featured · AI
                    </span>
                  )}
                  <h3 className="font-display text-xl md:text-2xl font-bold leading-tight mb-4">
                    {cap.title}
                  </h3>
                  <p className="text-sm text-[#4B5563] group-hover:text-white/70 leading-relaxed">
                    {cap.summary}
                  </p>
                  <ArrowUpRight className="absolute top-8 right-8 w-5 h-5 opacity-40 group-hover:opacity-100 group-hover:translate-x-1 group-hover:-translate-y-1 transition" />
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="py-24 md:py-32">
        <div className="container-page">
          <div className="mb-16">
            <p className="eyebrow mb-4">I · Industries</p>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter">
              Sector expertise, applied.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {INDUSTRIES.map((ind) => (
              <article
                key={ind.key}
                data-testid={`home-industry-${ind.key}`}
                className="group relative shine border border-[#e5e7eb] hover:border-[#1E5AF6] transition-all duration-300 bg-white box-elevated"
              >
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={ind.image}
                    alt={ind.name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1220]/60 to-transparent" />
                  <div className="absolute inset-0 bg-gradient-to-tr from-[#1E5AF6]/25 via-transparent to-[#16A374]/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500 mix-blend-screen" />
                  <p className="absolute bottom-4 left-4 text-white font-mono text-[10px] tracking-[0.2em] uppercase">
                    Sector · {String(INDUSTRIES.indexOf(ind) + 1).padStart(2, "0")}
                  </p>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-xl font-bold">{ind.name}</h3>
                  <p className="text-sm text-[#4B5563] mt-3 leading-relaxed line-clamp-4">
                    {ind.summary}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Big text marquee — motion band */}
      <section
        data-testid="home-text-marquee"
        className="py-10 md:py-14 border-y border-[#e5e7eb] overflow-hidden"
      >
        <TextMarquee
          items={[
            "Digital",
            "Technology",
            "Data",
            "AI",
            "GenAI",
            "Oracle Fusion",
            "Agentic Apps",
            "Cloud",
            "Analytics",
            "Copilots",
          ]}
        />
      </section>

      {/* ORACLE CTA STRIP */}
      <section
        data-testid="home-oracle-strip"
        className="relative bg-ink-deep text-white overflow-hidden"
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-25"
          style={{ backgroundImage: `url(${IMAGES.tech})`, backgroundSize: "cover", backgroundPosition: "center" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B1220] via-[#0B1220]/85 to-[#0E1E5A]/60" />
        <div aria-hidden className="pointer-events-none absolute inset-0">
          <div className="absolute -top-32 right-0 h-[380px] w-[380px] rounded-full bg-[#16A374]/25 blur-[100px] aurora" />
          <div className="absolute bottom-0 left-10 h-[420px] w-[420px] rounded-full bg-[#1E5AF6]/25 blur-[100px] aurora" />
        </div>
        <div className="container-page py-24 md:py-32 relative">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
            <div className="md:col-span-8">
              <p className="eyebrow text-white/70 mb-6">O · Oracle Partner</p>
              <h2 className="font-display text-4xl md:text-6xl font-extrabold tracking-tighter leading-[1.05]">
                Oracle Fusion Cloud —
                <br /> implemented, integrated,
                <span className="grad-text"> AI-enhanced.</span>
              </h2>
              <p className="mt-8 max-w-2xl text-white/70 text-lg leading-relaxed">
                We are Oracle partners for Fusion Cloud, delivering end-to-end
                implementations across Financials, Procurement, PPM, SCM, HRMS,
                EPM, OIC, OCI, and E-Business Suite — layered with AI copilots
                and analytics.
              </p>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Link
                to="/oracle"
                data-testid="home-oracle-cta"
                className="group relative shine shine-dark box-elevated-dark inline-flex items-center gap-2 bg-white text-[#0B1220] px-7 py-4 text-sm font-semibold hover:bg-[#1E5AF6] hover:text-white transition-colors"
              >
                See Oracle offerings
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="py-24 md:py-32">
        <div className="container-page grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <p className="eyebrow mb-4">Next step</p>
            <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter">
              Let&apos;s tie your transformation
              <br /> strategy to business outcomes.
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              to="/contact"
              data-testid="home-contact-cta"
              className="group relative shine box-elevated inline-flex items-center gap-2 bg-[#0B1220] text-white px-7 py-4 text-sm font-semibold hover:bg-[#1E5AF6] transition-colors"
            >
              Contact DigiTrans
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
